<h2 mat-dialog-title class="m-0 notif-title">
    @if (presentation) {
        <mat-icon class="title-icon {{ presentation.action }}">{{ presentation.icon }}</mat-icon>
        <span class="title-text">
            <span class="title-action">{{ presentation.action }}</span>
            <strong class="title-name">{{ presentation.name }}</strong>
        </span>
    } @else {
        <span class="title-text"><strong>Description</strong></span>
    }
    <!-- Close button. -->
    <button mat-icon-button mat-dialog-close class="close-button">
        <mat-icon>close</mat-icon>
    </button>
</h2>

<mat-dialog-content class="h-100-70 w-100 notif-content">

    <mat-tab-group class="notif-tabs" animationDuration="0ms">

        <!-- ─── Summary tab — friendly view ───────────────────────────── -->
        <mat-tab label="Summary">
            <div class="summary-pane">

                @if (task && presentation) {
                    <!-- Top facts grid: who / what / when / status -->
                    <div class="facts-grid">
                        <div class="fact">
                            <span class="fact-label"><mat-icon>person</mat-icon> Who</span>
                            <span class="fact-value">
                                <strong>{{ actorName }}</strong>
                                <span class="fact-meta">{{ actorRole }} this</span>
                            </span>
                        </div>

                        <div class="fact">
                            <span class="fact-label"><mat-icon>category</mat-icon> What</span>
                            <span class="fact-value">
                                <strong>{{ presentation.action }}</strong>
                                <span class="fact-meta">{{ presentation.name }}</span>
                            </span>
                        </div>

                        <div class="fact">
                            <span class="fact-label"><mat-icon>schedule</mat-icon> Created</span>
                            <span class="fact-value">
                                @if (task.createdTimeStamp) {
                                    <strong>{{ task.createdTimeStamp.toDate() | date:'medium' }}</strong>
                                } @else {
                                    <strong>—</strong>
                                }
                            </span>
                        </div>

                        <div class="fact">
                            <span class="fact-label"><mat-icon>event_available</mat-icon> Completed</span>
                            <span class="fact-value">
                                @if (task.completedTimestamp; as completed) {
                                    <strong>{{ completed.toDate() | date:'medium' }}</strong>
                                } @else {
                                    <strong class="muted">Not yet</strong>
                                }
                            </span>
                        </div>

                        <div class="fact">
                            <span class="fact-label">
                                <mat-icon class="title-status-icon {{ statusClass }}">{{ presentation.statusIcon }}</mat-icon>
                                Status
                            </span>
                            <span class="fact-value">
                                <strong class="status-pill {{ statusClass }}">{{ presentation.statusLabel }}</strong>
                                @if (task.run && task.run > 1) {
                                    <span class="fact-meta">attempt #{{ task.run }}</span>
                                }
                            </span>
                        </div>

                        @if (task.taskNumber) {
                            <div class="fact">
                                <span class="fact-label"><mat-icon>tag</mat-icon> Task #</span>
                                <span class="fact-value"><strong>{{ task.taskNumber }}</strong></span>
                            </div>
                        }
                    </div>

                    <!-- Description / error message -->
                    @if (task.description) {
                        <div class="block">
                            <h4>Description</h4>
                            <p class="block-text">{{ task.description }}</p>
                        </div>
                    }
                    @if (task.error) {
                        <div class="block error-block">
                            <h4><mat-icon>error</mat-icon> Error</h4>
                            <p class="block-text">{{ task.error }}</p>
                            @if (task.errorCode) {
                                <p class="block-meta">Code: <code>{{ task.errorCode }}</code></p>
                            }
                        </div>
                    }

                    <!-- Field-by-field changes -->
                    @if (hasChanges) {
                        <div class="block">
                            <h4>
                                @if (isDelete) {
                                    What was deleted ({{ changedFields.length }})
                                } @else {
                                    What changed ({{ changedFields.length }})
                                }
                            </h4>
                            <ul class="changes-list">
                                @for (f of changedFields; track f.key) {
                                    <li class="change-row" [class.long]="!f.isShort" [class.removal]="f.isRemoval">
                                        <span class="change-key">{{ f.key }}</span>
                                        @if (f.isRemoval) {
                                            <span class="change-pair removal">
                                                @if (f.isShort) {
                                                    <span class="removed">{{ f.before }}</span>
                                                } @else {
                                                    <span class="removed block">{{ f.before }}</span>
                                                }
                                            </span>
                                        } @else if (f.isShort) {
                                            <span class="change-pair">
                                                <span class="before">{{ f.before }}</span>
                                                <mat-icon class="arrow">arrow_forward</mat-icon>
                                                <span class="after">{{ f.after }}</span>
                                            </span>
                                        } @else {
                                            <span class="change-pair stacked">
                                                <span class="lbl">Before</span>
                                                <span class="before block">{{ f.before }}</span>
                                                <span class="lbl">After</span>
                                                <span class="after block">{{ f.after }}</span>
                                            </span>
                                        }
                                    </li>
                                }
                            </ul>
                        </div>
                    } @else {
                        <div class="block muted">
                            @if (isDelete) {
                                <p>This item was deleted. No additional details captured.</p>
                            } @else {
                                <p>No field-level diff available for this task.</p>
                            }
                        </div>
                    }
                } @else {
                    <p class="muted">No task details to display.</p>
                }

            </div>
        </mat-tab>

        <!-- ─── Raw JSON tab — developer-only ─────────────────────────── -->
        @if (isDeveloper) {
            <mat-tab label="Raw JSON">
                <div class="view-container m-3" [ngClass]="{'large-wrap': !allowOverflow}">
                    <div class="view-header">
                        <span class="viewer-title-spacer">
                            <!-- Max 5 set for performance reasons. -->
                            <mat-slider class="mt-3" min="0" max="5" step="1" color="accent" discrete="true">
                                <input matSliderThumb [(ngModel)]="depth">
                            </mat-slider>

                            <mat-slide-toggle class="ml-3 mt-3 t-10" color="primary" [(ngModel)]="allowOverflow">Show Overflow</mat-slide-toggle>
                        </span>
                    </div>

                    <div class="view-content">
                        <ngx-json-viewer [json]="data" [depth]="depth"></ngx-json-viewer>
                    </div>
                </div>
            </mat-tab>
        }

    </mat-tab-group>

</mat-dialog-content>

<mat-dialog-actions align="end">
    <button mat-button mat-dialog-close>Close</button>
</mat-dialog-actions>
